'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { EVENT_STATUS, EventStatus } from '@/lib/schemas/event';
import { categories } from '@/lib/constants';
import {
	ExternalLink,
	Calendar,
	MapPin,
	Clock,
	Users,
	Building2,
} from 'lucide-react';
import { Event } from '@/lib/hooks/useEvents';

interface EventCardProps {
	event: Event;
	isStandalone: boolean;
}

export function EventCard({ event, isStandalone }: EventCardProps) {
	const router = useRouter();
	const categoryInfo = categories.find((cat) => cat.id === event.category);

	const handleEventClick = (e: React.MouseEvent) => {
		e.preventDefault();
		if (event.isExternal && !isStandalone) {
			window.open(`https://thrughus.com/events/${event.source_id}`, '_blank');
		} else {
			router.push(
				event.isExternal
					? event.source === 'travel_guide'
						? `https://thrughus.com/events/${event.source_id}`
						: `/events/${event.id}`
					: `/events/${event.id}`
			);
		}
	};

	return (
		<Card className='group w-full rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] border-foreground/10 hover:border-primary/20 bg-gradient-to-br from-background to-background/50'>
			<Link
				href={`${event.isExternal ? `https://thrughus.com/events/${event.source_id}` : `/events/${event.id}`}`}
				target={event.isExternal ? '_blank' : '_self'}
			>
				{/* Image Section */}
				<div className='relative w-full aspect-[16/9] overflow-hidden'>
					<Image
						src={event.image_url}
						alt={event.title}
						fill
						className='object-cover transition-transform duration-500 group-hover:scale-110'
					/>

					{/* Gradient Overlay */}
					<div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent' />

					{/* Hover Overlay */}
					<div className='absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors duration-300' />

					{/* Category Badge */}
					<div className='absolute top-3 left-3'>
						<Badge
							variant='secondary'
							className='bg-background/95 text-foreground backdrop-blur-md border-0 shadow-lg hover:bg-background transition-colors'
						>
							{categoryInfo?.icon && (
								<categoryInfo.icon className='w-3 h-3 mr-1' />
							)}
							{categoryInfo?.name || event.category}
						</Badge>
					</div>

					{/* Event Type Badge */}
					<div className='absolute top-3 right-3'>
						{event.isExternal ? (
							<Badge
								variant='outline'
								className='bg-gradient-to-r from-green-100/90 to-emerald-100/90 text-green-800 border-green-200 backdrop-blur-sm'
							>
								<ExternalLink className='w-3 h-3 mr-1' />
								Travel Guide
							</Badge>
						) : event.status === EVENT_STATUS.PENDING ? (
							<Badge
								variant='outline'
								className='bg-yellow-100/90 text-yellow-800 border-yellow-200 backdrop-blur-sm'
							>
								<Clock className='w-3 h-3 mr-1' />
								Pending
							</Badge>
						) : (
							<Badge
								variant='outline'
								className='bg-blue-100/90 text-blue-800 border-blue-200 backdrop-blur-sm'
							>
								ThrughPass
							</Badge>
						)}
					</div>

					{/* Organization Badge */}
					<div className='absolute bottom-3 left-3'>
						<Badge
							variant='outline'
							className='bg-background/90 text-foreground backdrop-blur-sm border-0'
						>
							{event.organization.name}
						</Badge>
					</div>
				</div>

				{/* Event Info */}
				<div className='p-5 space-y-4'>
					{/* Title */}
					<h3 className='text-xl font-bold text-foreground line-clamp-2 group-hover:text-primary transition-colors duration-200 leading-tight'>
						{event.title}
					</h3>

					{/* Date and Time */}
					<div className='flex items-center gap-2 text-sm text-muted-foreground'>
						<Calendar className='w-4 h-4' />
						<span className='font-medium'>{event.date}</span>
					</div>

					{/* Location */}
					<div className='flex items-center gap-2 text-sm text-muted-foreground'>
						<MapPin className='w-4 h-4' />
						<span className='line-clamp-1'>{event.location}</span>
					</div>

					{/* Venue (only for internal events) */}
					{!event.isExternal && event.venue && (
						<div className='flex items-center gap-2 text-sm text-muted-foreground'>
							<Building2 className='w-4 h-4' />
							<span className='line-clamp-1'>{event.venue}</span>
						</div>
					)}

					{/* Tickets Info */}
					{!event.isExternal && (
						<div className='flex items-center gap-1 text-sm pt-2 border-t border-border/50'>
							<Users className='w-4 h-4 text-muted-foreground' />
							<span className='text-muted-foreground'>
								{event.ticketsLeft.toLocaleString()} tickets left
							</span>
						</div>
					)}

					{/* Action Button */}
					<Button
						className='w-full mt-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl group-hover:scale-[1.02]'
						onClick={handleEventClick}
					>
						{event.isExternal ? (
							<>
								<ExternalLink className='w-4 h-4 mr-2' />
								View on Travel Guide
							</>
						) : event.status === EVENT_STATUS.APPROVED ? (
							event.price === 'Free' ? (
								event.hasRsvpQuestions ? (
									<>RSVP for Event</>
								) : (
									<>Get Free Ticket</>
								)
							) : (
								<>Buy Ticket</>
							)
						) : (
							<>
								<Calendar className='w-4 h-4 mr-2' />
								View Event
							</>
						)}
					</Button>
				</div>
			</Link>
		</Card>
	);
}
